import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import PageHeader from '../components/PageHeader';
import toast from 'react-hot-toast';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Clock3,
  FileCheck2,
  History,
  LockKeyhole,
  ScanFace,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import MediaPane from '../components/MediaPane';
import { useReviewInbox } from '../components/ReviewInboxContext';
import { createBlobRegistry } from '../lib/blobRegistry.mjs';
import {
  allPanesLoaded,
  bundlePanels,
  canApproveBundle,
  canSubmitRejection,
  formatInvalidReferences,
  invalidReasonLabel,
  lifecycleColor,
  lifecycleLabel,
  paneNeedsLoad,
  slotLabel,
} from '../lib/docReviewLogic.mjs';
import { ErrorBox, SkeletonPage } from '../ui/kit';
import './Verifications.css';
import { datumUndZeit, nurDatum } from '../lib/zeit';

// Eine Seite je offener Verifizierung mit allen sechs Flächen gleichzeitig:
// Selfie, Profilfoto, Ausweis vorn/hinten und Führerschein vorn/hinten.
// Alle Inhalte werden authentifiziert als Blob geladen und beim Verlassen
// widerrufen. Die Entscheidung bleibt vollständig menschlich und fail-closed.
const fmtDate = value => nurDatum(value, { leer: '–' });
const fmtTime = value => datumUndZeit(value, { jahr: 'voll', leer: '–' });
const OPEN_STATES = ['submitted', 'auto_passed'];
const paneStateFor = (panel, panes) => panes[panel.key]?.forId
  === (panel.key === 'selfie' ? panel.selfie?.url : panel.reference?.document_id);

// FAHRER-KI-BACKEND-001 (Teil E): KI-Abgleich lesbar machen — Urteil je Referenz + Begründung.
const VERDICT_LABEL = { same: 'gleiche Person', different: 'andere Person', unclear: 'nicht beurteilbar' };
const AI_HEADLINE = {
  same: 'KI-Abgleich: gleiche Person auf allen Referenzen — automatisch bestanden (Owner-Freigabe), hier widerrufbar.',
  different: 'KI-Abgleich: andere Person — keine Sperre aus dem Vergleich. Du entscheidest.',
  different_locked: 'KI-Abgleich: andere Person — der Fahrer ist SOFORT gesperrt. Du entscheidest.',
  unclear: 'KI-Abgleich: nicht beurteilbar — Prüfung wie bisher durch dich.',
  unavailable: 'KI-Abgleich nicht gelaufen (kein freigegebenes Profilbild oder Anbieter nicht erreichbar) — Prüfung wie bisher.',
  disabled: 'KI-Abgleich ist abgeschaltet — Prüfung wie bisher.',
  unsupported: 'KI-Abgleich unvollständig — eine gebundene Referenz liegt nicht als Bild vor (z. B. PDF). Prüfung wie bisher durch dich.',
  error: 'KI-Abgleich fehlgeschlagen — Prüfung wie bisher.',
};

// Rückmeldung nach dem Knopf: „gesperrt" NUR, wenn der Lauf wirklich gesperrt hat (REVIEW-FIX-005).
export function rerunToast({ status, acted }) {
  if (acted === 'locked' || acted === 'already_locked') return 'KI: andere Person — Fahrer gesperrt, du entscheidest';
  if (acted === 'auto_passed') return 'KI: gleiche Person — automatisch bestanden';
  if (acted === 'retry_requested') return 'KI: neues Foto erbeten';
  if (acted === 'human') return `KI-Abgleich: ${VERDICT_LABEL[status] || status} — keine Wirkung, du entscheidest`;
  if (acted === 'stale' || acted === 'auto_passed_final' || acted === 'locked_final') return 'KI-Abgleich: nur Notiz, Entscheidung bleibt';
  return `KI-Abgleich: ${status || 'erneut gelaufen'}`;
}

function AiMatchPanel({ ai, accountLock, verificationStatus, onRerun, busy, manualOnly }) {
  if (!ai) return null;
  const tone = ai.light === 'red' ? 'is-critical' : ai.light === 'green' ? 'is-ok' : 'is-warning';
  const legacy = !!(ai.gate && ai.gate.legacy_client);
  // „gesperrt" NUR bei echter Sperre aus dem Vergleich (acted), nie aus dem Urteil allein (REVIEW-FIX-005).
  const lockedByAi = ai.acted === 'locked' || ai.acted === 'already_locked';
  const statusLine = AI_HEADLINE[ai.status === 'different' && lockedByAi ? 'different_locked' : ai.status] || `KI-Abgleich: ${ai.status}`;
  const headline = manualOnly
    ? 'Dokument nachgereicht — du entscheidest nach dem vollständigen Vergleich.'
    : ai.acted === 'retry_requested'
    ? 'KI-Abgleich (1. Foto): nicht eindeutig — der Fahrer wurde um ein neues Selfie gebeten, kein Eingriff nötig.'
    : ai.stale
      ? `${statusLine} Ergebnis kam NACH dem Entscheid${ai.evidence_changed ? ' bzw. zu einer älteren Einreichung' : ''} — ohne Wirkung.`
      : legacy
        ? `KI-Abgleich (App ohne KI-Vertrag): ${VERDICT_LABEL[ai.status] || ai.status} — nur ein Hinweis, keine Sperre aus dem Vergleich, du entscheidest.`
        : `${ai.stage === 2 ? 'Zweites Foto — ' : ''}${statusLine}`;
  // Wiederholen nur, solange die KI noch entscheiden darf: eingereicht und weder gesperrt noch bestanden (final).
  const canRerun = !manualOnly && verificationStatus === 'submitted' && ai.acted !== 'locked' && ai.acted !== 'auto_passed';
  return (
    <div className={`verification-review-alert verification-review-ai ${tone}`} data-testid="ai-match">
      <div><ScanFace size={14} /> {headline}</div>
      <section>
        {Object.entries(ai.results || {}).map(([key, r]) => (
          <p key={key}>• {r.label || slotLabel(key)}: <strong>{VERDICT_LABEL[r.verdict] || r.verdict}</strong>{r.reason ? ` — ${r.reason}` : ''}</p>
        ))}
        {ai.error_code && <p>Grund: {ai.error_code}</p>}
        {accountLock && accountLock.kind === 'selfie' && (
          <p><strong>Fahrer ist gesperrt (Selfie).</strong> Freigeben löst die Sperre sofort; Ablehnen lässt sie bestehen.</p>
        )}
        {accountLock && accountLock.kind !== 'selfie' && <p>Fahrer ist außerdem {accountLock.label}.</p>}
        <p className="verification-review-ai-meta">{ai.model ? `Modell ${ai.model}` : ''}{ai.at ? ` · ${fmtTime(ai.at)}` : ''}</p>
      </section>
      {canRerun && <button className="verification-review-retry" onClick={onRerun} disabled={busy}>KI-Abgleich wiederholen</button>}
    </div>
  );
}

export default function VerificationReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const inbox = useReviewInbox();
  const registry = useRef(null);
  const loadVersion = useRef(0);
  const paneVersions = useRef({});
  if (!registry.current) registry.current = createBlobRegistry();

  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [panes, setPanes] = useState({});
  const [confirmed, setConfirmed] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  // 409 reference_invalid bleibt sichtbar und sperrt die Freigabe bis zur
  // erneuten Einreichung des Fahrers.
  const [invalidRefs, setInvalidRefs] = useState([]);

  const loadBlob = useCallback(async (key, apiPath, forId) => {
    const version = (paneVersions.current[key] || 0) + 1;
    paneVersions.current[key] = version;
    setPanes(current => ({ ...current, [key]: { loading: true, forId } }));
    try {
      const response = await api.get(apiPath.replace(/^\/api/, ''), { responseType: 'blob' });
      if (paneVersions.current[key] !== version) return;
      const url = registry.current.register(key, response.data);
      setPanes(current => ({
        ...current,
        [key]: { url, mime: response.data.type, forId },
      }));
    } catch (loadError) {
      if (paneVersions.current[key] !== version) return;
      setPanes(current => ({
        ...current,
        [key]: { error: true, forId, retryPath: apiPath },
      }));
    }
  }, []);

  const load = useCallback(() => {
    const version = ++loadVersion.current;
    setError(false);
    return api.get(`/admin/verifications/${id}/review-bundle`)
      .then(response => { if (loadVersion.current === version) setData(response.data); })
      .catch(() => { if (loadVersion.current === version) setError(true); });
  }, [id]);

  useEffect(() => {
    load();
    return () => {
      loadVersion.current++;
      for (const key of Object.keys(paneVersions.current)) paneVersions.current[key]++;
      registry.current.revokeAll();
    };
  }, [load]);

  // Die Signatur koppelt jede geladene Fläche an genau die Snapshot-Revision,
  // die der Server für diese Verifizierung geliefert hat.
  const refSignature = JSON.stringify([
    data?.verification_id,
    data?.bundle?.reference_review_token,
    data?.bundle?.selfie && !data.bundle.selfie.missing ? data.bundle.selfie.url : null,
    ...(data?.bundle?.references || []).map(reference => reference.missing ? null : reference.document_id),
  ]);

  useEffect(() => {
    const bundle = data?.bundle;
    if (!bundle) return;
    setConfirmed(false);
    setInvalidRefs([]);
    if (
      bundle.selfie
      && !bundle.selfie.missing
      && bundle.selfie.url
      && paneNeedsLoad(panes.selfie, bundle.selfie.url)
    ) {
      loadBlob('selfie', bundle.selfie.url, bundle.selfie.url);
    }
    for (const ref of bundle.references || []) {
      if (
        !ref.missing
        && ref.preview_url
        && paneNeedsLoad(panes[ref.key], ref.document_id)
      ) {
        loadBlob(
          ref.key,
          ref.content_url || ref.preview_url,
          ref.document_id,
        );
      }
    }
    // refSignature ist die bewusst verdichtete Abhängigkeit aus den
    // servergebundenen Referenz-IDs.
  }, [refSignature]);

  const approve = async () => {
    setBusy(true);
    try {
      if (data?.bundle?.manual_review_only) {
        await api.patch(`/admin/verifications/${id}/approve`, {
          reference_review_token: data.bundle.reference_review_token,
          comparison_confirmed: confirmed === true,
        });
      } else await api.patch(`/admin/verifications/${id}/approve`);
      toast.success('Verifizierung bestätigt');
      inbox.selfieDecided(id);
      navigate('/verifications');
    } catch (requestError) {
      const detail = requestError.response?.data;
      if (['reference_review_changed', 'reference_supplement_stale', 'verification_stale'].includes(detail?.code)) {
        setConfirmed(false);
        toast.error('Die Prüfung hat sich geändert — bitte neu laden und den Vergleich erneut bestätigen.');
        load();
      } else if (detail?.code === 'missing_reference') {
        setInvalidRefs([]);
        toast.error(
          `Freigabe nicht möglich — freigegebene Referenz fehlt: ${(detail.missing || []).join(', ')}`,
          { style: { whiteSpace: 'pre-line' } },
        );
        load();
      } else if (detail?.code === 'reference_invalid') {
        setInvalidRefs(detail.invalid || []);
        toast.error(formatInvalidReferences(detail.invalid), {
          duration: 9000,
          style: { whiteSpace: 'pre-line' },
        });
        load();
      } else if (detail?.code === 'already_decided') {
        toast.error('Bereits entschieden — Ansicht wird aktualisiert.');
        load();
      } else {
        toast.error(detail?.error || 'Fehler bei der Freigabe');
      }
    } finally {
      setBusy(false);
    }
  };

  const supplementReference = async reference => {
    const candidate = reference.supplement_candidate;
    if (!candidate || !data?.bundle?.can_supplement) return;
    setBusy(true);
    setConfirmed(false);
    try {
      await api.post(`/admin/verifications/${id}/reference-supplements`, {
        key: reference.key, document_id: candidate.document_id, document_revision: candidate.revision,
        reference_review_token: data.bundle.reference_review_token,
      });
      toast.success('Dokument ergänzt — bitte alle Flächen prüfen und anschließend entscheiden.');
      await load();
    } catch (requestError) {
      toast.error(requestError.response?.data?.error || 'Antwort konnte nicht bestätigt werden. Die Prüfung wird neu geladen.');
      await load();
    } finally { setBusy(false); }
  };

  const reject = async () => {
    if (!canSubmitRejection(reason)) {
      toast.error('Ablehnungsgrund ist Pflicht');
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/admin/verifications/${id}/reject`, { reason: reason.trim() });
      toast(data?.selfie_protocol === 'driver_selfie_review_v1' ? 'Abgelehnt — der Fahrer bleibt bis zur menschlichen Freigabe offline' : 'Abgelehnt — der Fahrer muss sich neu verifizieren');
      inbox.selfieDecided(id);
      navigate('/verifications');
    } catch (requestError) {
      const detail = requestError.response?.data;
      if (detail?.code === 'reason_required') {
        toast.error('Ablehnungsgrund ist Pflicht');
      } else if (detail?.code === 'already_decided') {
        toast.error('Bereits entschieden — Ansicht wird aktualisiert.');
        load();
      } else {
        toast.error(detail?.error || 'Fehler beim Ablehnen');
      }
    } finally {
      setBusy(false);
    }
  };

  // FAHRER-KI-BACKEND-001 (Teil E): Abgleich erneut anstoßen (kein Entscheid; Ergebnis kommt in den Bundle).
  const rerunAi = async () => {
    setBusy(true);
    try {
      const response = await api.post(`/admin/verifications/${id}/ai-match`);
      toast.success(rerunToast(response.data || {}));
      load();
    } catch (rerunError) {
      toast.error(rerunError.response?.data?.error || 'KI-Abgleich fehlgeschlagen');
    } finally { setBusy(false); }
  };

  if (error) return <ErrorBox text="Verifizierung konnte nicht geladen werden." onRetry={load} />;
  if (!data || data.verification_id !== id) return <SkeletonPage />;

  const bundle = data.bundle;
  const panels = bundlePanels(bundle);
  const open = OPEN_STATES.includes(data.status) || data.selfie_review_required === true;
  const missingKeys = bundle?.missing_references || [];
  const panesReady = allPanesLoaded(bundle, panes);
  const panesBound = panels.every(panel => panel.missing || paneStateFor(panel, panes));
  const approveEnabled = open
    && canApproveBundle(bundle, confirmed) && panesReady
    && (!bundle.manual_review_only || panesBound)
    && invalidRefs.length === 0;
  const paneState = key => panes[key] || {};
  const loadedPaneCount = panels.filter(panel => panel.missing || !!paneState(panel.key).url).length;
  const statusLabel = data.status === 'approved'
    ? 'Bestätigt'
    : data.status === 'rejected'
      ? 'Abgelehnt'
      : data.status === 'auto_passed'
        ? 'Liveness ok · Stichprobe'
        : data.status === 'submitted'
          ? 'Prüfung offen'
          : data.status;

  return (
    <div className="verification-review-page">
      <button className="verification-review-back" onClick={() => navigate('/verifications')}>
        <ArrowLeft size={14} /> Zurück zur Queue
      </button>
      <PageHeader
        title="Selfie-Vergleich"
        description={<>{bundle?.driver?.name || `Fahrer #${data.driver_id}`} · {bundle?.driver?.email || 'Keine E-Mail'} · Verifizierung #{data.verification_id}</>}
        actions={
          <div className="verification-review-heading-status">
            <span className={`verifications-status is-${data.status === 'approved' ? 'approved' : data.status === 'rejected' ? 'rejected' : 'pending'}`}>
              <i /> {statusLabel}
            </span>
            <small><LockKeyhole size={11} /> {bundle?.manual_review_only ? 'Originalnachweis und geprüfte Ergänzung' : 'Snapshotgebundene Referenzen'}</small>
          </div>
        }
      />

      <section className="verification-review-principle">
        <span><ShieldCheck size={18} /></span>
        <div>
          <strong>Du entscheidest – kein automatischer biometrischer Abgleich</strong>
          <p>Alle sechs Flächen bleiben gleichzeitig sichtbar. Eine Freigabe ist erst nach vollständig geladenem Vergleich und deiner Bestätigung möglich.</p>
        </div>
      </section>

      <AiMatchPanel ai={bundle?.ai_match} accountLock={bundle?.account_lock} verificationStatus={data.status} onRerun={rerunAi} busy={busy} manualOnly={bundle?.manual_review_only} />

      {bundle?.manual_review_only && (
        <div className="verification-review-alert is-warning" data-testid="reference-supplements">
          <div><FileCheck2 size={14} /> Dokument nachgereicht — manuelle Prüfung erforderlich</div>
          <p>Originalselfie und Fotoversuch bleiben erhalten. Prüfe alle Flächen und bestätige anschließend den Vergleich.</p>
          {(bundle.reference_supplements || []).map(entry => (
            <p key={entry.id}>{slotLabel(entry.key)} · Revision r{entry.revision} · ergänzt {fmtTime(entry.created_at)} durch {entry.actor_name || 'Admin'}</p>
          ))}
        </div>
      )}

      <section className="verification-review-stats">
        <div><span><FileCheck2 size={14} /></span><small>Vergleichsflächen</small><strong>6</strong><p>Selfie plus fünf Referenzen</p></div>
        <div><span><CheckCircle2 size={14} /></span><small>Geladen / sichtbar</small><strong>{loadedPaneCount}/6</strong><p>{panesReady ? 'Vergleich vollständig geladen' : 'Fail-Closed bis vollständig'}</p></div>
        <div><span><AlertTriangle size={14} /></span><small>Fehlende Seiten</small><strong>{missingKeys.length}</strong><p>{missingKeys.length ? 'Freigabe bleibt gesperrt' : 'Keine fehlende Pflichtseite'}</p></div>
        <div><span><Clock3 size={14} /></span><small>Offene Erneuerungen</small><strong>{(bundle?.pending_renewals || []).length}</strong><p>Nicht Teil dieses Snapshots</p></div>
      </section>

      {missingKeys.length > 0 && (
        <div className="verification-review-alert is-critical">
          <div><AlertTriangle size={14} /> Freigegebene Pflichtseite fehlt — Freigeben bleibt deaktiviert</div>
          <p>Fehlend: {missingKeys.map(slotLabel).join(', ')}. Nach Upload und Dokumentfreigabe kannst du die geprüfte Version hier ausdrücklich ergänzen. Danach prüfst du den vollständigen Vergleich und entscheidest.</p>
          {open && bundle.can_supplement && (bundle.references || []).filter(ref => ref.missing && ref.supplement_candidate).map(ref => (
            <section key={ref.key}>
              <p>{slotLabel(ref.key)} · freigegebene Revision r{ref.supplement_candidate.revision}{ref.supplement_candidate.approved_at ? ` · ${fmtTime(ref.supplement_candidate.approved_at)}` : ''}</p>
              <button className="verification-review-retry" disabled={busy}
                onClick={() => supplementReference(ref)} aria-label={`${slotLabel(ref.key)} als geprüftes Dokument ergänzen`}>
                Geprüftes Dokument ergänzen
              </button>
            </section>
          ))}
          <button className="verification-review-retry" onClick={load} disabled={busy}>Dokumentstatus aktualisieren</button>
        </div>
      )}

      {invalidRefs.length > 0 && (
        <div className="verification-review-alert is-critical">
          <div><AlertTriangle size={14} /> Referenz ungültig — Freigeben bleibt deaktiviert</div>
          <section>
            {invalidRefs.map(item => (
              <p key={item.key}>• {item.label || slotLabel(item.key)}: {invalidReasonLabel(item.reason)}</p>
            ))}
            <p>{bundle?.manual_review_only
              ? 'Bitte den Dokumentstatus prüfen und die Ansicht neu laden. Originalnachweis und Ergänzung bleiben gebunden.'
              : 'Die Verifizierung ist an die beim Selfie festgeschriebenen Revisionen gebunden — erst nach erneuter Einreichung des Fahrers wieder bestätigbar.'}</p>
          </section>
        </div>
      )}

      {(bundle?.pending_renewals || []).length > 0 && (
        <div className="verification-review-alert is-warning">
          <div>Offene Erneuerung eingereicht — nicht Teil dieses Vergleichs</div>
          <p>
            {bundle.pending_renewals.map(item => `${slotLabel(item.key)} (r${item.revision})`).join(' · ')} — die Prüfung vergleicht ausschließlich die beim Selfie festgeschriebenen, freigegebenen Revisionen.
          </p>
        </div>
      )}

      <section className="verification-review-comparison">
        <header>
          <div>
            <span className="verifications-overline">Snapshot · sechs Flächen gleichzeitig</span>
            <h2>Visueller Identitätsvergleich</h2>
          </div>
          <span><LockKeyhole size={12} /> Autorisiert geladen · beim Verlassen verworfen</span>
        </header>

        <div className="verification-review-grid">
          {panels.map(panel => {
            const state = paneState(panel.key);
            const reference = panel.reference;
            const isSelfie = panel.key === 'selfie';
            const sub = isSelfie
              ? panel.selfie
                ? `Eingereicht ${fmtTime(panel.selfie.submitted_at)} · ${panel.selfie.method === 'liveness' ? 'Liveness-Signal' : panel.selfie.method || ''}`
                : undefined
              : reference
                ? `Revision r${reference.revision} · ${lifecycleLabel(reference.lifecycle_status)}${reference.expires_at ? ` · gültig bis ${fmtDate(reference.expires_at)}` : ''}`
                : undefined;
            return (
              <div key={panel.key} className={`verification-review-pane ${isSelfie ? 'is-selfie' : ''}`}>
                <MediaPane
                  label={panel.label}
                  sub={sub}
                  url={state.url}
                  mime={state.mime}
                  loading={state.loading}
                  missing={panel.missing || !!state.error}
                  missingText={panel.missing ? 'Keine freigegebene Version vorhanden' : state.error ? 'Datei konnte nicht geladen werden' : undefined}
                  height={isSelfie ? 380 : 300}
                  footer={<>
                    {reference?.source === 'supplement' && (
                      <div className="verification-review-last-decision">Beim Selfie fehlend · nachträglich geprüftes Dokument ergänzt</div>
                    )}
                    {!panel.missing && state.error && (
                      <button
                        className="verification-review-retry"
                        onClick={() => loadBlob(
                          panel.key,
                          state.retryPath || (isSelfie ? panel.selfie?.url : (reference?.content_url || reference?.preview_url)),
                          state.forId,
                        )}
                      >
                        Erneut laden
                      </button>
                    )}
                    {reference && (reference.decisions || []).length > 0 && (
                      <div className="verification-review-last-decision">
                        Letzte Entscheidung: {reference.decisions[reference.decisions.length - 1].decision === 'approved' ? 'freigegeben' : 'abgelehnt'} ({fmtTime(reference.decisions[reference.decisions.length - 1].decided_at)})
                      </div>
                    )}
                  </>}
                />
              </div>
            );
          })}
        </div>
      </section>

      <section className="verification-review-context-card">
        <header><History size={14} /><span>Prüfkontext und revisionsgebundene Historie</span></header>
        <div className="verification-review-context-grid">
          <div className="verification-review-context-block">
            <small>Fahrer</small>
            <strong>{bundle?.driver?.name || '–'}</strong>
            <span>{bundle?.driver?.email || 'Keine E-Mail'}</span>
          </div>
          <div className="verification-review-context-block">
            <small>Referenzen · Revision und Ablauf</small>
            {(bundle?.references || []).map(reference => (
              <p key={reference.key} className={reference.missing ? 'is-missing' : ''}>
                {reference.label || slotLabel(reference.key)}: {reference.missing ? 'fehlt' : `r${reference.revision}${reference.expires_at ? ` · bis ${fmtDate(reference.expires_at)}` : ''}`}
              </p>
            ))}
          </div>
          <div className="verification-review-context-block">
            <small>Frühere Entscheidungen</small>
            {(bundle?.previous_verifications || []).length === 0 && <span>Keine früheren Verifizierungen.</span>}
            {(bundle?.previous_verifications || []).slice(0, 5).map(previous => {
              const color = lifecycleColor(
                previous.status === 'approved'
                  ? 'approved'
                  : previous.status === 'rejected'
                    ? 'rejected'
                    : 'pending_review',
              );
              return (
                <p key={previous.id}>
                  <span style={{ fontWeight: 700, color: color.color }}>
                    {previous.status === 'approved' ? 'Bestätigt' : previous.status === 'rejected' ? 'Abgelehnt' : previous.status}
                  </span>
                  {' '}#{previous.id} · {fmtTime(previous.reviewed_at || previous.submitted_at)}{previous.rejection_reason ? ` · Grund: ${previous.rejection_reason}` : ''}
                </p>
              );
            })}
          </div>
        </div>

        {open && (
          <div className="verification-review-decision">
            <div className="verification-review-decision-copy">
              <span><ShieldCheck size={15} /></span>
              <div><small>Manuelle Entscheidung</small><strong>Vergleich abschließen</strong></div>
            </div>
            <label className="verification-review-confirm">
              <input type="checkbox" checked={confirmed} disabled={bundle?.manual_review_only && (!panesReady || !panesBound)} onChange={event => setConfirmed(event.target.checked)} />
              <span>
                <strong>Vergleich bestätigt</strong>
                <small>{bundle?.retained_selfie_review === true ? 'Das Selfie wurde nach Ablauf der Aufbewahrungsfrist gelöscht. Ich habe die vorhandenen Referenzen und den Prüfverlauf geprüft und verantworte die menschliche Freigabe ohne erneute Aufnahme.'
                  : bundle?.manual_review_only ? 'Ich habe das Originalselfie und alle Referenzen einschließlich des nachgereichten Dokuments nebeneinander geprüft.'
                    : 'Ich habe Selfie, Profilfoto, Ausweis und Führerschein nebeneinander geprüft.'}</small>
              </span>
            </label>
            <label className="verification-review-reason">
              <span>Ablehnungsgrund · für den Fahrer sichtbar</span>
              <input value={reason} onChange={event => setReason(event.target.value)} placeholder="Pflicht bei Ablehnung …" />
            </label>
            <div className="verification-review-actions">
              <button
                className="is-reject"
                onClick={reject}
                disabled={busy || !OPEN_STATES.includes(data.status) || !canSubmitRejection(reason)}
                title={!canSubmitRejection(reason) ? 'Ablehnungsgrund ist Pflicht' : undefined}
              >
                <XCircle size={14} /> Ablehnen
              </button>
              <button
                className="is-approve"
                onClick={approve}
                disabled={busy || !approveEnabled}
                title={!approveEnabled
                  ? missingKeys.length
                    ? 'Freigegebene Pflichtseite fehlt'
                    : invalidRefs.length
                      ? 'Eine Referenz ist ungültig geworden — erst nach erneuter Einreichung bestätigbar'
                      : !panesReady
                        ? 'Nicht alle Flächen geladen — erst laden bzw. „Erneut laden" nutzen'
                        : 'Bitte zuerst „Vergleich bestätigt" setzen'
                  : undefined}
              >
                <CheckCircle2 size={14} /> Freigeben
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
